import { ReactNode } from "react"
import { cn } from "@/lib/utils"

type StageAccent = "enrollment" | "consent" | "carePlan" | "checkIn" | "billing"

const accents: Record<
  StageAccent,
  { bar: string; bg: string; text: string; number: string }
> = {
  enrollment: {
    bar: "bg-info",
    bg: "bg-info/10",
    text: "text-info",
    number: "bg-info/10 text-info border-info/30",
  },
  consent: {
    bar: "bg-warning",
    bg: "bg-warning/10",
    text: "text-warning",
    number: "bg-warning/10 text-warning border-warning/30",
  },
  carePlan: {
    bar: "bg-primary",
    bg: "bg-primary/5",
    text: "text-primary",
    number: "bg-primary/10 text-primary border-primary/20",
  },
  checkIn: {
    bar: "bg-primary",
    bg: "bg-accent",
    text: "text-primary",
    number: "bg-accent text-primary border-primary/30",
  },
  billing: {
    bar: "bg-success",
    bg: "bg-success/10",
    text: "text-success",
    number: "bg-success/10 text-success border-success/30",
  },
}

export interface StageProps {
  number: number
  accent: StageAccent
  icon: React.ElementType
  title: string
  description: string
  total: number
  children?: ReactNode
  /** Optional "connector" to render below this stage linking to the next */
  showConnector?: boolean
}

export function Stage({
  number,
  accent,
  icon: Icon,
  title,
  description,
  total,
  children,
  showConnector = true,
}: StageProps) {
  const a = accents[accent]
  return (
    <div className="relative">
      <div className="relative overflow-hidden rounded-2xl border border-border/60 bg-card shadow-sm">
        {/* Left accent bar */}
        <div className={cn("absolute left-0 top-0 bottom-0 w-1", a.bar)} />

        {/* Header */}
        <div className={cn("px-4 py-4 md:px-6 md:py-5 border-b border-border/40 flex items-center gap-3 md:gap-4", a.bg)}>
          <div
            className={cn(
              "flex h-10 w-10 items-center justify-center rounded-full border text-sm font-bold tabular-nums",
              a.number,
            )}
          >
            {number}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <Icon className={cn("h-4 w-4 shrink-0", a.text)} />
              <h2 className="text-base font-semibold text-foreground">{title}</h2>
            </div>
            <p className="text-xs text-muted-foreground mt-0.5">{description}</p>
          </div>
          <div className="shrink-0 text-right">
            <div className={cn("text-2xl md:text-3xl font-bold tabular-nums leading-none", a.text)}>{total}</div>
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground mt-1">
              in stage
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="p-4 md:p-5 space-y-4">{children}</div>
      </div>

      {/* Connector to next stage */}
      {showConnector && (
        <div className="flex justify-center h-6">
          <div className="w-px bg-border" />
        </div>
      )}
    </div>
  )
}

/** Small pill used for state breakdowns inside stages. */
export function StatePill({
  label,
  count,
  tone = "neutral",
  onClick,
}: {
  label: string
  count: number
  tone?: "neutral" | "blue" | "amber" | "emerald" | "red" | "violet"
  onClick?: () => void
}) {
  const tones: Record<string, string> = {
    neutral: "bg-muted text-muted-foreground border-border",
    blue: "bg-info/10 text-info border-info/30",
    amber: "bg-warning/10 text-warning border-warning/30",
    emerald: "bg-success/10 text-success border-success/30",
    red: "bg-destructive/10 text-destructive border-destructive/30",
    violet: "bg-accent text-primary border-primary/30",
  }
  const classes = cn(
    "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[11px] font-medium tabular-nums",
    tones[tone],
    onClick && "cursor-pointer hover:brightness-95",
  )
  const content = (
    <>
      <span className="font-semibold">{count}</span>
      <span>{label}</span>
    </>
  )
  if (onClick) {
    return (
      <button type="button" onClick={onClick} className={classes}>
        {content}
      </button>
    )
  }
  return <span className={classes}>{content}</span>
}

